// Transitions PR-1 — the loan's Transitions tab: the workflow home for the
// seven exit paths (payoff / refinance / assumption / resale / right-size /
// hardship / BMSP). One open transition per loan (D-20); payoff-family
// scheduling happens only through the irrevocable Prepayment Notice (§2.5).
// PR-3: execution is bound — running the payoff completes a Scheduled
// payoff-family transition (and refuses when the open transition is the wrong
// family or hasn't recorded its notice), so the Scheduled card carries the
// primary "Execute payoff" action prefilled with the tender date.

import { useState } from "react";
import type { FormEvent } from "react";
import {
  Button,
  Card,
  EmptyState,
  Label,
  Skeleton,
  StatusBadge,
  Textarea,
  type StatusTone,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency } from "@/design-system/lib/format";
import { formatDate } from "../common/colorBand";
import { Link } from "react-router-dom";
import { PayoffModal } from "../paymentTracking/PayoffModal";
import { ExecuteAssumptionWizard } from "../transitions-wizard/ExecuteAssumptionWizard";
import { TransitionStartWizard } from "../transitions-wizard/TransitionStartWizard";
import { TransitionExplainModal } from "../transitions-wizard/TransitionExplainModal";
import { ExecuteBmspExtensionModal } from "../transitions/ExecuteBmspExtensionModal";
import { BmspCountdownCard } from "../transitions/BmspCountdownCard";
import { ENGINE_HANDOFF, PATH_FACTS } from "../transitions-wizard/transitionsWizardCopy";
import { PayoffQuoteModal } from "../transitions/PayoffQuoteModal";
import { PrepaymentNoticeModal } from "../transitions/PrepaymentNoticeModal";
import { CancelTransitionModal } from "../transitions/CancelTransitionModal";
import {
  TRANSITION_STATUS_LABEL,
  TRANSITION_TYPE_INFO,
  isPayoffFamily,
  type LoanTransitionDto,
  type LoanTransitionStatus,
} from "@/api/portfolioAdmin/loanTransitionsApi";
import {
  useBmspCountdown,
  useChangeLoanTransitionStatus,
  useLinkableDeals,
  useLoanTransitionDetail,
  useLoanTransitions,
} from "@/hooks/portfolioAdmin/useLoanTransitions";
import { useAuth } from "@/hooks/useAuth";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

const TERMINAL: ReadonlySet<LoanTransitionStatus> = new Set([
  "Completed",
  "Canceled",
  "Lapsed",
]);

const STATUS_TONE: Record<LoanTransitionStatus, StatusTone> = {
  Draft: "neutral",
  Initiated: "progress",
  AwaitingLinkedDeal: "info",
  PendingApproval: "warn",
  Scheduled: "onyx",
  Completed: "ok",
  Canceled: "muted",
  Lapsed: "danger",
};

/** Plain-words consequence per advance target (the TransitionStateControl
 *  TRANSITION_CONSEQUENCE pattern) — shown in the confirm modal. */
const ADVANCE_CONSEQUENCE: Partial<Record<LoanTransitionStatus, string>> = {
  Initiated: "Marks the transition as actively in progress.",
  AwaitingLinkedDeal:
    "Parks this transition until the incoming buyer's deal closes. You can link the deal now or later.",
  PendingApproval: "Sends the transition for review and approval.",
  Scheduled:
    "Approves the transition and schedules the closing. You can set a target closing date.",
  Completed:
    "Records the transition as completed for bookkeeping. The payoff / resale / right-size / assumption executions stamp completion automatically — use this only to correct a record the engines could not bind.",
};

/**
 * WALL-CALENDAR renderer, pinned to UTC. Correct for `tenderDate` and the payoff
 * quote's `asOfDate`, which arrive as bare `yyyy-MM-dd` from `DateOnly` columns —
 * the `length === 10` branch exists for exactly that wire form.
 *
 * 🔴 It is NOT for instants, and three call sites used to pass one:
 * `noticeDeliveredAt` and every timeline `at` are `DateTimeOffset.UtcNow`, so a
 * transition recorded after 5pm Phoenix rendered as TOMORROW. Those now use
 * `formatDate` (timezone-aware). Do not "simplify" by routing them back here.
 */
function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function TenderCountdown({ transition }: { transition: LoanTransitionDto }) {
  if (transition.tenderDate == null || transition.daysToTender == null) return null;
  const days = transition.daysToTender;
  const tone: StatusTone = days < 0 ? "danger" : days <= 7 ? "danger" : days <= 30 ? "warn" : "info";
  const label =
    days < 0
      ? `Tender date passed ${-days} day${days === -1 ? "" : "s"} ago`
      : days === 0
        ? "Tender due today"
        : `${days} day${days === 1 ? "" : "s"} to tender`;
  return (
    <StatusBadge tone={tone} data-testid="tender-countdown">
      {label} · {fmtDate(transition.tenderDate)}
    </StatusBadge>
  );
}

/** Inline confirm-with-consequence modal for a plain status advance. */
function AdvanceStatusModal({
  loanId,
  transition,
  target,
  onClose,
}: {
  loanId: string;
  transition: LoanTransitionDto;
  target: LoanTransitionStatus;
  onClose: () => void;
}) {
  const mutation = useChangeLoanTransitionStatus(loanId);
  const [reason, setReason] = useState("");
  const [linkedDealId, setLinkedDealId] = useState("");
  const [closingDate, setClosingDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  // PR-3 — the in-progress deal picker replaces the raw GUID input.
  const linkableDeals = useLinkableDeals(target === "AwaitingLinkedDeal");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mutation.mutateAsync({
        transitionId: transition.id,
        body: {
          targetStatus: target,
          reason: reason.trim() || null,
          linkedDealId: target === "AwaitingLinkedDeal" && linkedDealId.trim() ? linkedDealId.trim() : null,
          scheduledClosingDate: target === "Scheduled" && closingDate ? closingDate : null,
        },
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Move to ${TRANSITION_STATUS_LABEL[target]}`}
      description={ADVANCE_CONSEQUENCE[target] ?? undefined}
    >
      <form onSubmit={submit} className="flex flex-col gap-space-3" data-testid="advance-status-form">
        {target === "AwaitingLinkedDeal" && (
          <div>
            <Label htmlFor="linked-deal-id">Incoming deal (optional)</Label>
            <select
              id="linked-deal-id"
              className="w-full rounded border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
              value={linkedDealId}
              onChange={(e) => setLinkedDealId(e.target.value)}
              data-testid="linked-deal-picker"
            >
              <option value="">
                {linkableDeals.isLoading ? "Loading deals…" : "Link now or later"}
              </option>
              {(linkableDeals.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.dealNumber}
                  {d.businessName ? ` — ${d.businessName}` : ""} ({d.stage})
                </option>
              ))}
            </select>
          </div>
        )}
        {target === "Scheduled" && (
          <div>
            <Label htmlFor="closing-date">Target closing date (optional)</Label>
            <input
              id="closing-date"
              type="date"
              className="rounded border border-border-light bg-white-pure px-space-2 py-space-1 text-small block"
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
            />
          </div>
        )}
        <div>
          <Label htmlFor="advance-note">Note (optional)</Label>
          <Textarea
            id="advance-note"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {error && <p className="text-small text-status-error m-0">{error}</p>}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" disabled={mutation.isPending} data-testid="advance-status-submit">
            {mutation.isPending ? "Saving…" : `Move to ${TRANSITION_STATUS_LABEL[target]}`}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function TransitionsTab({ loan }: { loan: LoanSummaryDto }) {
  const auth = useAuth();
  const canManage =
    auth.hasPermission("Transitions.Manage") || auth.hasRoleCode("ADMIN");
  const loanIsLive =
    (loan.accountState ?? "") !== "paid-off" &&
    (loan.accountState ?? "") !== "charged-off" &&
    loan.currentBalance > 0;
  const canPayoff =
    (auth.hasPermission("Payoff.Initiate") || auth.hasRoleCode("ADMIN")) && loanIsLive;
  // PR-4a — executing an assumption is an identity boundary with its own gate.
  const canExecuteAssumption =
    auth.hasPermission("Transitions.ExecuteAssumption") || auth.hasRoleCode("ADMIN");
  // PR-4b — the amendment-grade BMSP extension has its own gate too.
  const canExecuteExtension =
    auth.hasPermission("Transitions.ExecuteExtension") || auth.hasRoleCode("ADMIN");

  const listQuery = useLoanTransitions(loan.id);
  const bmsp = useBmspCountdown(loan.id);
  const transitions = listQuery.data ?? [];
  const open = transitions.find((t) => !TERMINAL.has(t.status));
  const history = transitions.filter((t) => TERMINAL.has(t.status));
  const detailQuery = useLoanTransitionDetail(loan.id, open?.id);
  const detail = detailQuery.data;

  const [startOpen, setStartOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [payoffOpen, setPayoffOpen] = useState(false);
  const [assumptionOpen, setAssumptionOpen] = useState(false);
  const [extensionOpen, setExtensionOpen] = useState(false);
  const [askAiOpen, setAskAiOpen] = useState(false);
  const [advanceTarget, setAdvanceTarget] = useState<LoanTransitionStatus | null>(null);

  if (listQuery.isLoading) {
    return (
      <Card data-testid="transitions-loading">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-5 w-full mt-space-2" />
      </Card>
    );
  }

  const payoffFamilyOpen = open != null && isPayoffFamily(open.type);
  const canRecordNotice =
    canManage &&
    payoffFamilyOpen &&
    (open!.status === "Draft" || open!.status === "Initiated");
  // PR-4b — families with a real executor: the engine stamps Completed, so
  // the generic advance-to-Completed correction button is suppressed for them.
  const executorBackedOpen =
    open != null &&
    (open.type === "SaleWithAssumption" ||
      open.type === "HardshipExit" ||
      open.type === "BmspMaturityExtension");
  // Plain advances exclude Canceled (its own modal), Lapsed (PR-3's worker
  // owns the missed-tender flip), and — for the payoff family — Scheduled
  // (only the Prepayment Notice schedules a payoff).
  const advanceTargets = (open?.legalNextStatuses ?? []).filter(
    (s) =>
      s !== "Canceled" &&
      s !== "Lapsed" &&
      !(s === "Scheduled" && payoffFamilyOpen) &&
      !(s === "Completed" && executorBackedOpen),
  );

  return (
    <div className="flex flex-col gap-space-4" data-testid="transitions-tab">
      {/* Header actions */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-space-2">
          <div>
            <h3 className="font-display font-bold text-h3 m-0">Transitions</h3>
            <p className="text-small text-gray-mid m-0">
              Payoff, refinance, assumption, resale, right-size, hardship, and
              balloon-maturity paths — one open transition per loan.
            </p>
          </div>
          <div className="flex flex-wrap gap-space-2">
            <Button
              variant="secondary"
              onClick={() => setQuoteOpen(true)}
              data-testid="payoff-quote-button"
            >
              Payoff quote
            </Button>
            {canPayoff && (
              <Button
                variant="secondary"
                onClick={() => setPayoffOpen(true)}
                data-testid="transitions-record-payoff-button"
              >
                Record payoff
              </Button>
            )}
            {canManage && loanIsLive && (
              <Button
                onClick={() => (open ? setBlockOpen(true) : setStartOpen(true))}
                data-testid="start-transition-button"
              >
                Start a transition
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* BMSP covenant countdown (balloon loans only — null otherwise) */}
      {bmsp.data && <BmspCountdownCard countdown={bmsp.data} />}

      {/* Open transition */}
      {open ? (
        <Card data-testid="open-transition-card">
          <div className="flex flex-wrap items-start justify-between gap-space-2">
            <div>
              <div className="flex items-center gap-space-2">
                <h4 className="font-display font-bold text-h4 m-0">
                  {TRANSITION_TYPE_INFO[open.type].label}
                </h4>
                <StatusBadge tone={STATUS_TONE[open.status]} data-testid="open-transition-status">
                  {TRANSITION_STATUS_LABEL[open.status]}
                </StatusBadge>
                <TenderCountdown transition={open} />
              </div>
              <p className="text-small text-gray-mid m-0 mt-space-1 max-w-prose">
                {TRANSITION_TYPE_INFO[open.type].description}
              </p>
              {open.noticeDeliveredAt && (
                <p className="text-small m-0 mt-space-1" data-testid="notice-recorded-line">
                  Prepayment Notice recorded {formatDate(open.noticeDeliveredAt)} —{" "}
                  <span className="text-status-error font-semibold">irrevocable</span>; failure
                  to tender by {fmtDate(open.tenderDate)} is an immediate event of default.
                </p>
              )}
              {detail?.noticeQuote && (
                <p className="text-small text-gray-mid m-0 mt-space-1">
                  Binding payoff statement: {formatCurrency(detail.noticeQuote.totalPayoff)} as
                  of {fmtDate(detail.noticeQuote.asOfDate)}.
                </p>
              )}
              {open.linkedDealId && detail?.linkedDealStage && (
                <p className="text-small m-0 mt-space-1" data-testid="linked-deal-line">
                  Linked deal {detail.linkedDealNumber ?? open.linkedDealId}:{" "}
                  <span className="font-semibold">{detail.linkedDealStage}</span>
                  {detail.linkedDealStage === "Closed" &&
                    open.status === "AwaitingLinkedDeal" &&
                    " — the deal has closed; move this transition to Pending approval."}
                </p>
              )}
            </div>
            {canManage && (
              <div className="flex flex-wrap gap-space-2">
                {canRecordNotice && (
                  <Button onClick={() => setNoticeOpen(true)} data-testid="record-notice-button">
                    Record Prepayment Notice
                  </Button>
                )}
                {canPayoff && payoffFamilyOpen && open.status === "Scheduled" && (
                  <Button
                    onClick={() => setPayoffOpen(true)}
                    data-testid="execute-payoff-button"
                  >
                    Execute payoff
                  </Button>
                )}
                {canExecuteAssumption &&
                  (open.type === "SaleWithAssumption" || open.type === "HardshipExit") &&
                  open.status === "Scheduled" && (
                    <Button
                      onClick={() => setAssumptionOpen(true)}
                      data-testid="execute-assumption-button"
                    >
                      Execute assumption…
                    </Button>
                  )}
                {canExecuteExtension &&
                  open.type === "BmspMaturityExtension" &&
                  open.status === "Scheduled" && (
                    <Button
                      onClick={() => setExtensionOpen(true)}
                      data-testid="execute-bmsp-extension-button"
                    >
                      Execute extension…
                    </Button>
                  )}
                {(open.type === "PhoenixBackedResale" || open.type === "RightSizeRefinance") && (
                  <Link
                    to={ENGINE_HANDOFF[open.type].route(loan.id)}
                    className="inline-flex"
                    data-testid="engine-wizard-link"
                  >
                    <Button variant="secondary">{ENGINE_HANDOFF[open.type].cta}</Button>
                  </Link>
                )}
                {advanceTargets.map((target) => (
                  <Button
                    key={target}
                    variant="secondary"
                    onClick={() => setAdvanceTarget(target)}
                    data-testid={`advance-${target}`}
                  >
                    {TRANSITION_STATUS_LABEL[target]}
                  </Button>
                ))}
                <Button
                  variant="secondary"
                  onClick={() => setCancelOpen(true)}
                  data-testid="cancel-transition-button"
                >
                  Cancel…
                </Button>
                <Button
                  variant="text"
                  onClick={() => setAskAiOpen(true)}
                  data-testid="transition-ask-ai-button"
                >
                  Ask AI
                </Button>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="mt-space-3">
            <h5 className="text-small font-semibold m-0 mb-space-1">Timeline</h5>
            {detailQuery.isLoading ? (
              <Skeleton className="h-5 w-2/3" />
            ) : (detail?.timeline.length ?? 0) === 0 ? (
              <p className="text-small text-gray-mid m-0">No events yet.</p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-1" data-testid="transition-timeline">
                {detail!.timeline.map((e) => (
                  <li key={e.id} className="text-small">
                    <span className="text-gray-mid">{formatDate(e.at)}</span>{" "}
                    <span>{e.note ?? e.kind}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      ) : (
        <EmptyState
          title="No open transition"
          description={
            loanIsLive
              ? "Start a transition to track this loan's exit path — payoff quote first if the buyer is exploring options."
              : "This loan is retired — transitions apply to live loans only."
          }
        />
      )}

      {/* History */}
      {history.length > 0 && (
        <Card data-testid="transition-history">
          <h4 className="font-display font-bold text-h4 m-0 mb-space-2">History</h4>
          <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
            {history.map((t) => (
              <li key={t.id} className="text-small flex items-center gap-space-2">
                <StatusBadge tone={STATUS_TONE[t.status]}>
                  {TRANSITION_STATUS_LABEL[t.status]}
                </StatusBadge>
                <span>{TRANSITION_TYPE_INFO[t.type].label}</span>
                <span className="text-gray-mid">
                  {formatDate(t.completedAt ?? t.canceledAt ?? t.createdAt)}
                  {t.cancelReason ? ` — ${t.cancelReason}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Modals */}
      <TransitionStartWizard loan={loan} open={startOpen} onClose={() => setStartOpen(false)} />
      {/* D-20 — one open transition per loan. The button stays visible; when a
          transition is already open, name it in a blocking dialog rather than
          opening the start wizard (surfacing the server-side rule client-side). */}
      {open && blockOpen && (
        <Modal
          open
          onClose={() => setBlockOpen(false)}
          title="A transition is already open"
          size="sm"
        >
          <div className="flex flex-col gap-space-3" data-testid="transition-block-dialog">
            <p className="text-small m-0" data-testid="transition-block-message">
              This loan already has an open transition:{" "}
              <span className="font-semibold">{TRANSITION_TYPE_INFO[open.type].label}</span> (
              {TRANSITION_STATUS_LABEL[open.status]}). Cancel it before starting another.
            </p>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setBlockOpen(false)}
                data-testid="transition-block-close"
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <PayoffQuoteModal
        loanId={loan.id}
        canManage={canManage}
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
      />
      {open && (
        <PrepaymentNoticeModal
          loanId={loan.id}
          transitionId={open.id}
          open={noticeOpen}
          onClose={() => setNoticeOpen(false)}
        />
      )}
      {open && cancelOpen && (
        <CancelTransitionModal
          loanId={loan.id}
          transitionId={open.id}
          requiresOverride={payoffFamilyOpen && open.status === "Scheduled"}
          open={cancelOpen}
          onClose={() => setCancelOpen(false)}
        />
      )}
      {open && advanceTarget && (
        <AdvanceStatusModal
          loanId={loan.id}
          transition={open}
          target={advanceTarget}
          onClose={() => setAdvanceTarget(null)}
        />
      )}
      {open && assumptionOpen && (
        <ExecuteAssumptionWizard
          loan={loan}
          transition={open}
          open={assumptionOpen}
          onClose={() => setAssumptionOpen(false)}
        />
      )}
      {open && extensionOpen && (
        <ExecuteBmspExtensionModal
          loanId={loan.id}
          transition={open}
          open={extensionOpen}
          onClose={() => setExtensionOpen(false)}
        />
      )}
      {open && askAiOpen && (
        <TransitionExplainModal
          loanId={loan.id}
          context={{
            pathType: open.type,
            pathFacts: PATH_FACTS[open.type],
            stepKey: "transition-card",
            transitionId: open.id,
          }}
          onClose={() => setAskAiOpen(false)}
        />
      )}
      <PayoffModal
        loanId={loan.id}
        open={payoffOpen}
        onClose={() => setPayoffOpen(false)}
        initialDate={
          payoffFamilyOpen && open?.status === "Scheduled"
            ? (open.tenderDate ?? undefined)
            : undefined
        }
      />
    </div>
  );
}
